import { getIdToken } from '@/services/auth';
import type { paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

export type LinkProviderRequestBody =
  paths['/v2/auth/link-provider']['post']['requestBody']['content']['application/json'];

export type LinkProviderSuccessResponse =
  paths['/v2/auth/link-provider']['post']['responses']['200']['content']['application/json'];

/**
 * Manually link accounts during NPI/email conflict resolution.
 *
 * Used when user encounters signup conflict, signs in to existing account, and completes
 * linking flow. Requires stored context with duplicateUserId and duplicateIdToken from
 * the conflict. Backend links OAuth identity to existing account (same Cognito behavior
 * as reconcileProvider). Existing account remains primary; OAuth becomes secondary identity.
 *
 * Backend should validate that the Authorization token's user ID matches duplicateUserId
 * and that duplicateIdToken belongs to duplicateUserId. Returns error codes:
 * - MISMATCH: Token doesn't match expected account
 * - EXPIRED_TOKEN: Token has expired
 * - INVALID_TOKEN: Token is invalid
 * - ALREADY_LINKED: Accounts are already linked
 *
 * Caller should validate user IDs before calling and validate response user ID matches
 * duplicateUserId to prevent incorrect linking.
 */
export async function linkProvider(body: LinkProviderRequestBody) {
  const idToken = await getIdToken();

  const { data } = await fetchClient.POST('/v2/auth/link-provider', {
    body,
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined,
  });

  return data;
}
